import { useState } from "react";
import { type OrgRole, type Standing, type TeamMember, api } from "../lib/api.ts";
import { useLoad } from "../lib/useLoad.ts";
/**
 * A team's own people, managed by its owner.
 *
 * Without this screen self-serve is only half true: a team can register itself
 * but then has to ask us to add its second person. The API already allowed it
 * (`/api/v1/orgs/:id/members`); only the tenant-facing screen was missing.
 */
import { Badge, Button, Card, InfoBanner, Input, Page, Select, Table } from "../ui/index.ts";

const ROLES: Array<{ value: OrgRole; label: string; holds: string }> = [
	{ value: "owner", label: "Owner", holds: "Manages people. Cannot be removed." },
	{ value: "manager", label: "Manager", holds: "Acts on what the team was lent." },
	{ value: "member", label: "Member", holds: "Reads what the team was lent." },
];

export function Team({ standing }: { standing: Standing }) {
	const team = standing.team;
	const orgId = team?.id ?? "";
	const [members, reload] = useLoad(() =>
		orgId ? api.teamMembers(orgId) : Promise.resolve({ members: [] }),
	);
	const [userId, setUserId] = useState("");
	const [email, setEmail] = useState("");
	const [role, setRole] = useState<OrgRole>("member");
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const isOwner = team?.role === "owner";

	async function run(action: () => Promise<unknown>) {
		setBusy(true);
		setError(null);
		try {
			await action();
			reload();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setBusy(false);
		}
	}

	if (!team) return null;

	return (
		<Page
			title={team.name}
			description={
				isOwner
					? "Add the people who work with you. They see exactly what the team has been lent — nothing of ours, and nothing of another team's."
					: "The people in your team. Only the owner can change this list."
			}
			actions={<Badge variant="info">You are {team.role}</Badge>}
		>
			{error && <InfoBanner variant="danger" title={error} />}

			<Table<TeamMember>
				columns={[
					{
						id: "who",
						header: "Person",
						accessor: (m) => (
							<>
								<div className="font-medium text-content">{m.email ?? "—"}</div>
								<div className="font-mono text-xs text-muted">{m.userId}</div>
							</>
						),
					},
					{
						id: "role",
						header: "Role",
						accessor: (m) =>
							isOwner ? (
								<Select
									value={m.role}
									disabled={busy}
									onChange={(e) =>
										void run(() =>
											api.setTeamMember(orgId, m.userId, { role: e.target.value as OrgRole }),
										)
									}
									options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
								/>
							) : (
								<span className="capitalize">{m.role}</span>
							),
					},
					{
						id: "status",
						header: "Status",
						accessor: (m) => (
							<Badge variant={m.status === "active" ? "success" : "warning"}>{m.status}</Badge>
						),
					},
					{
						id: "actions",
						header: "",
						accessor: (m) =>
							isOwner && m.role !== "owner" ? (
								<Button
									variant="ghost"
									size="sm"
									disabled={busy}
									onClick={() => void run(() => api.removeTeamMember(orgId, m.userId))}
								>
									Remove
								</Button>
							) : null,
					},
				]}
				data={members.state === "ready" ? members.data.members : []}
				keyExtractor={(m) => m.id}
				failed={members.state === "failed"}
				loading={members.state === "loading"}
				emptyMessage="Just you so far."
			/>

			{isOwner && (
				<Card className="mt-6 p-5">
					<p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-muted">
						Add someone
					</p>
					<form
						className="flex flex-wrap items-end gap-3"
						onSubmit={(e) => {
							e.preventDefault();
							if (!userId.trim()) return;
							void run(async () => {
								await api.setTeamMember(orgId, userId.trim(), { role, email: email.trim() });
								setUserId("");
								setEmail("");
							});
						}}
					>
						<Input
							label="Their G1 user id"
							value={userId}
							onChange={(e) => setUserId(e.target.value)}
							helperText="They need a G1 account already — ask them to sign in here once"
						/>
						<Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
						<Select
							label="Role"
							value={role}
							onChange={(e) => setRole(e.target.value as OrgRole)}
							options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
						/>
						<Button type="submit" loading={busy} disabled={!userId.trim()}>
							Add
						</Button>
					</form>
					<dl className="mt-5 space-y-1 text-xs text-muted">
						{ROLES.map((r) => (
							<div key={r.value} className="flex gap-2">
								<dt className="w-20 font-semibold text-content">{r.label}</dt>
								<dd>{r.holds}</dd>
							</div>
						))}
					</dl>
				</Card>
			)}
		</Page>
	);
}
